import { useEffect, useState } from 'react';
import { useTenantService, TenantLifecycleStatus, toTenantLifecycleStatus, tenantLifecycleStatusToNumber } from './tenantService';
import { TenantCard } from './TenantCard';
import { SearchableList } from '../shared/SearchableList';
import {
  Box, FormControl, InputLabel, Select, MenuItem, OutlinedInput, SelectChangeEvent,
  Checkbox, ListItemText, Button, Paper, Typography, FormControlLabel
} from '@mui/material';

export const TenantsList = () => {
  const tenantService = useTenantService();

  const [tenants, setTenants] = useState<any>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<TenantLifecycleStatus[]>([TenantLifecycleStatus.Active]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<TenantLifecycleStatus>(TenantLifecycleStatus.Active);
  const [updating, setUpdating] = useState(false);

  const loadTenants = () => {
    const needDeleted = selectedStatuses.includes(TenantLifecycleStatus.Deleted);
    tenantService.getAll(needDeleted).then(setTenants);
  };

  useEffect(() => {
    loadTenants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses]);

  const allStatuses = Object.values(TenantLifecycleStatus);
  const allSelected = selectedStatuses.length === allStatuses.length;

  const handleStatusChange = (event: SelectChangeEvent<TenantLifecycleStatus[]>) => {
    const value = event.target.value;
    if (value.includes('__all__' as any)) {
      setSelectedStatuses(allSelected ? [] : allStatuses);
    } else {
      setSelectedStatuses(typeof value === 'string' ? value.split(',') as TenantLifecycleStatus[] : value);
    }
  };

  const filteredTenants = tenants?.filter((t: any) => {
    if (selectedStatuses.length === 0) return true;
    const status = toTenantLifecycleStatus(t.lifecycleStatus);
    return selectedStatuses.includes(status);
  });

  const handleSelectChange = (id: string, selected: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredTenants?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTenants?.map((t: any) => t.id) || []));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;

    setUpdating(true);
    try {
      const updatePromises = Array.from(selectedIds).map(async (id) => {
        const tenant = tenants.find((t: any) => t.id === id);
        if (tenant) {
          await tenantService.put(id, { ...tenant, lifecycleStatus: tenantLifecycleStatusToNumber(bulkStatus) });
        }
      });
      await Promise.all(updatePromises);
      setSelectedIds(new Set());
      loadTenants();
    } catch (error) {
      console.error('Failed to update tenants:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 2, mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel>Lifecycle Status</InputLabel>
          <Select
            multiple
            value={selectedStatuses}
            onChange={handleStatusChange}
            input={<OutlinedInput label="Lifecycle Status" />}
            renderValue={(selected) => allSelected ? 'Alle' : selected.join(', ')}
          >
            <MenuItem value={'__all__' as any}>
              <Checkbox checked={allSelected} indeterminate={selectedStatuses.length > 0 && !allSelected} />
              <ListItemText primary="Velg alle" />
            </MenuItem>
            {allStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={selectedStatuses.includes(status)} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedIds.size > 0 && (
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedIds.size === filteredTenants?.length && filteredTenants?.length > 0}
                indeterminate={selectedIds.size > 0 && selectedIds.size < filteredTenants?.length}
                onChange={handleSelectAll}
              />
            }
            label={`Velg alle synlige (${filteredTenants?.length || 0})`}
          />
        </Box>
      )}

      {selectedIds.size > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography>
              {selectedIds.size} tenant{selectedIds.size > 1 ? 's' : ''} valgt
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white', borderRadius: 1 }}>
              <Select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value as TenantLifecycleStatus)}
              >
                {allStatuses.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBulkUpdate}
              disabled={updating}
            >
              {updating ? 'Oppdaterer...' : 'Sett status'}
            </Button>
            <Button
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
              onClick={() => setSelectedIds(new Set())}
            >
              Avbryt
            </Button>
          </Box>
        </Paper>
      )}

      <SearchableList
        list={filteredTenants}
        renderElement={(t: any) => (
          <TenantCard
            allTenants={tenants}
            key={t.id}
            tenant={t}
            selectable={true}
            isSelected={selectedIds.has(t.id)}
            onSelectChange={handleSelectChange}
          />
        )}
      />
    </>
  );
}
